class StickyAddToCart extends HTMLElement {
  constructor() {
    super();
    this.sectionId = this.dataset.sectionId;
    this.formId = this.dataset.productFormId;
    this.submitButton = this.querySelector('[data-sticky-submit]');
    this.priceEl = this.querySelector('[data-sticky-price]');
    this.imageEl = this.querySelector('.sticky-add-to-cart__image');
    this.optionsContainer = this.querySelector('[data-sticky-options]');
    this.optionGroups = Array.from(this.querySelectorAll('[data-sticky-option]'));

    this.onOptionClick = this.onOptionClick.bind(this);
    this.onVariantChange = this.onVariantChange.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);

    this.optionGroups.forEach((group) => this.setupSizeGroup(group));
    this.buildColorDropdown();

    if (this.submitButton) {
      this.submitButton.addEventListener('click', this.onSubmit);
    }

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange);
    }

    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('keydown', this.onKeydown);

    this.setupVisibilityObserver();
  }

  disconnectedCallback() {
    this.variantChangeUnsubscriber?.();
    this.visibilityObserver?.disconnect();
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keydown', this.onKeydown);
  }

  // ---- Size (real Shopify variant option) ----

  setupSizeGroup(group) {
    group.querySelectorAll('[data-option-value-id]').forEach((button) => {
      button.addEventListener('click', this.onOptionClick);
    });
    this.setupDropdownToggle(group);
  }

  onOptionClick(event) {
    const button = event.currentTarget;
    if (button.classList.contains('is-unavailable')) return;

    const valueId = button.dataset.optionValueId;
    const variantSelects = document.querySelector(`variant-selects[data-section="${this.sectionId}"]`);
    if (!variantSelects) return;

    const target = variantSelects.querySelector(`[data-option-value-id="${valueId}"]`);
    if (!target) return;

    if (target.tagName === 'INPUT') {
      target.click();
    } else if (target.tagName === 'OPTION') {
      const select = target.closest('select');
      if (select) {
        select.value = target.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    this.closeAllDropdowns();
  }

  onVariantChange({ data }) {
    if (!data || data.sectionId !== this.sectionId) return;
    const { variant } = data;

    const variantSelects = document.querySelector(`variant-selects[data-section="${this.sectionId}"]`);
    if (variantSelects) {
      const selectedIds = Array.from(
        variantSelects.querySelectorAll('input:checked, select option[selected]')
      ).map((el) => el.dataset.optionValueId);

      this.optionGroups.forEach((group) => this.syncTriggerFromSelection(group, selectedIds));
    }

    if (this.priceEl) {
      const sourcePrice = document.getElementById(`price-${this.sectionId}`);
      if (sourcePrice) this.priceEl.innerHTML = sourcePrice.innerHTML;
    }

    if (this.imageEl && variant?.featured_media?.preview_image?.src) {
      this.imageEl.src = variant.featured_media.preview_image.src;
    }

    const disabled = !variant || variant.available === false;
    if (this.submitButton) {
      this.submitButton.disabled = disabled;
      const textEl = this.submitButton.querySelector('[data-sticky-submit-text]');
      if (textEl) {
        textEl.textContent = !variant
          ? this.dataset.textUnavailable
          : disabled
            ? this.dataset.textSoldOut
            : this.dataset.textAddToCart;
      }
    }
  }

  syncTriggerFromSelection(group, selectedIds) {
    const trigger = group.querySelector('[data-sticky-option-trigger]');
    const triggerLabel = group.querySelector('[data-sticky-trigger-label]');
    const triggerSwatch = group.querySelector('[data-sticky-trigger-swatch]');

    group.querySelectorAll('[data-option-value-id]').forEach((button) => {
      const isSelected = selectedIds.includes(button.dataset.optionValueId);
      button.classList.toggle('is-selected', isSelected);

      if (isSelected && trigger) {
        const label = button.querySelector('span:last-child')?.textContent ?? '';
        if (triggerLabel) triggerLabel.textContent = label;

        const swatch = button.querySelector('.sticky-add-to-cart__swatch');
        if (swatch && triggerSwatch) {
          triggerSwatch.style.setProperty('--swatch-background', swatch.style.getPropertyValue('--swatch-background'));
          triggerSwatch.hidden = false;
        } else if (triggerSwatch) {
          triggerSwatch.hidden = true;
        }
      }
    });
  }

  onSubmit() {
    // this.formId comes from a data attribute that has been unreliable (renders blank in some
    // theme states), so don't depend on it alone — fall back to locating the real add-to-cart
    // form via the section id instead, which is always populated correctly.
    let form = this.formId ? document.getElementById(this.formId) : null;
    if (!form) {
      const productForm = document.querySelector(
        `product-form[data-section-id="${this.sectionId}"], [data-section-id="${this.sectionId}"] product-form`
      );
      form = productForm
        ? productForm.querySelector('form[data-type="add-to-cart-form"]')
        : document.querySelector(
            `#ProductInfo-${this.sectionId} form[data-type="add-to-cart-form"]`
          );
    }
    if (form) form.requestSubmit();
  }

  // ---- Color (not a real variant option on this store: separate linked products via an
  // existing color-swatch app block). Built by reading that block's already-rendered links. ----

  buildColorDropdown() {
    if (!this.optionsContainer) return;

    const existingWrapper = document.querySelector('.color-swatch-wrapper');
    if (existingWrapper) {
      this.buildColorDropdownFrom(existingWrapper);
      return;
    }

    // The color-swatch block is rendered by a third-party app script, which can run after
    // this element has already initialized. Wait for it to appear instead of giving up.
    const observer = new MutationObserver(() => {
      const wrapper = document.querySelector('.color-swatch-wrapper');
      if (wrapper) {
        observer.disconnect();
        this.buildColorDropdownFrom(wrapper);
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
    setTimeout(() => observer.disconnect(), 8000);
  }

  buildColorDropdownFrom(wrapper) {
    const links = Array.from(wrapper.querySelectorAll('a.color-swatch-custom'));
    if (!links.length) return;

    const currentName = this.dataset.colorName || '';
    const currentHex = this.dataset.colorHex || '';

    const group = document.createElement('div');
    group.className = 'sticky-add-to-cart__option';
    group.setAttribute('data-sticky-option', '');

    const trigger = document.createElement('button');
    trigger.type = 'button';
    trigger.className = 'sticky-add-to-cart__option-trigger';
    trigger.setAttribute('data-sticky-option-trigger', '');
    trigger.setAttribute('aria-haspopup', 'listbox');
    trigger.setAttribute('aria-expanded', 'false');

    const triggerSwatch = document.createElement('span');
    triggerSwatch.className = 'sticky-add-to-cart__swatch';
    triggerSwatch.setAttribute('data-sticky-trigger-swatch', '');
    this.applyColorSwatch(triggerSwatch, currentHex);

    const triggerLabel = document.createElement('span');
    triggerLabel.textContent = currentName;

    const caret = document.createElement('span');
    caret.className = 'svg-wrapper sticky-add-to-cart__caret';
    caret.innerHTML =
      '<svg viewBox="0 0 10 6" xmlns="http://www.w3.org/2000/svg"><path d="M9.354.646a.5.5 0 0 0-.708 0L5 4.293 1.354.646a.5.5 0 0 0-.708.708l4 4a.5.5 0 0 0 .708 0l4-4a.5.5 0 0 0 0-.708" fill="currentColor"/></svg>';

    trigger.append(triggerSwatch, triggerLabel, caret);

    const list = document.createElement('ul');
    list.className = 'sticky-add-to-cart__option-list';
    list.setAttribute('data-sticky-option-list', '');
    list.setAttribute('role', 'listbox');
    list.hidden = true;

    links.forEach((link) => {
      const isActive = link.classList.contains('active');

      const li = document.createElement('li');
      li.setAttribute('role', 'option');

      const anchor = document.createElement('a');
      anchor.href = link.href;
      anchor.className = 'sticky-add-to-cart__option-item';
      if (isActive) anchor.classList.add('is-selected');

      const swatch = document.createElement('span');
      swatch.className = 'sticky-add-to-cart__swatch';

      const label = document.createElement('span');
      label.textContent = isActive ? currentName : '';

      if (isActive) {
        this.applyColorSwatch(swatch, currentHex);
      } else {
        this.fetchColorData(link.href).then(({ name, hex }) => {
          if (name) label.textContent = name;
          this.applyColorSwatch(swatch, hex);
        });
      }

      anchor.append(swatch, label);
      li.append(anchor);
      list.append(li);
    });

    group.append(trigger, list);
    this.optionsContainer.prepend(group);
    this.optionGroups.unshift(group);
    this.setupDropdownToggle(group);
  }

  applyColorSwatch(swatchEl, hex) {
    swatchEl.style.setProperty('--swatch-background', hex || '#ccc');
  }

  async fetchColorData(url) {
    try {
      const response = await fetch(url);
      if (!response.ok) return { name: null, hex: null };
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const el = doc.querySelector('sticky-add-to-cart');
      return {
        name: el?.dataset.colorName || null,
        hex: el?.dataset.colorHex || null,
      };
    } catch (error) {
      return { name: null, hex: null };
    }
  }

  // ---- Dropdown open/close (shared by both option types) ----

  setupDropdownToggle(group) {
    const trigger = group.querySelector('[data-sticky-option-trigger]');
    if (!trigger) return;
    trigger.addEventListener('click', (event) => {
      event.stopPropagation();
      const isOpen = group.classList.contains('is-open');
      this.closeAllDropdowns();
      if (!isOpen) this.openDropdown(group);
    });
  }

  openDropdown(group) {
    const trigger = group.querySelector('[data-sticky-option-trigger]');
    const list = group.querySelector('[data-sticky-option-list]');
    group.classList.add('is-open');
    trigger?.setAttribute('aria-expanded', 'true');
    if (list) list.hidden = false;
  }

  closeAllDropdowns() {
    this.optionGroups.forEach((group) => {
      const trigger = group.querySelector('[data-sticky-option-trigger]');
      const list = group.querySelector('[data-sticky-option-list]');
      group.classList.remove('is-open');
      trigger?.setAttribute('aria-expanded', 'false');
      if (list) list.hidden = true;
    });
  }

  onDocumentClick(event) {
    if (!this.contains(event.target)) this.closeAllDropdowns();
  }

  onKeydown(event) {
    if (event.key === 'Escape') this.closeAllDropdowns();
  }

  // ---- Visibility (scroll past / back up) ----

  setupVisibilityObserver() {
    const targetId = this.dataset.observeTarget;
    const target = targetId ? document.getElementById(targetId) : null;
    if (!target) return;

    this.visibilityObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        // Show only once the original button has scrolled above the viewport (scrolled past it going down).
        // Hide again as soon as it's back in view (scrolled back up).
        const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        this.classList.toggle('is-visible', scrolledPast);
        if (!scrolledPast) this.closeAllDropdowns();
      },
      { threshold: 0 }
    );
    this.visibilityObserver.observe(target);
  }
}

customElements.define('sticky-add-to-cart', StickyAddToCart);
